import { useReactiveVar } from '@apollo/client'
import styled from '@emotion/styled'
import React, { ReactNode, useMemo } from 'react'

import { currencyCode } from '@emico/currency-code'
import { i18n } from '@emico/i18n'
import { overrideOptional } from '@emico/overrides'
import { theme } from '@emico/styles'

export type CurrencyDisplay = 'symbol' | 'code' | 'name' | 'none'
export type PriceVariant = 'old' | 'regular' | 'sale'

const currencyDisplayDefault = process.env
  .REACT_APP_PRICE_CURRENCY_DISPLAY as CurrencyDisplay
const decimalDashDefault = process.env.REACT_APP_PRICE_DECIMAL_DASH === 'true'
const useGroupingDefault = process.env.REACT_APP_PRICE_USE_GROUPING === 'true'

const RegularContainer = styled.div``

const SaleContainer = styled.div`
  color: ${theme.secondaryColor};
`

const OldContainer = styled.div`
  text-decoration: line-through;
`

interface PriceContainerProps {
  variant: PriceVariant
  children: ReactNode
}

const PriceContainer = ({ variant, ...props }: PriceContainerProps) => {
  if (variant === 'old') {
    return <OldContainer {...props} />
  }
  if (variant === 'sale') {
    return <SaleContainer {...props} />
  }
  return <RegularContainer {...props} />
}

interface Props {
  value: number
  currency?: string
  currencyDisplay?: CurrencyDisplay
  decimalDash?: boolean
  useGrouping?: boolean
  language?: string
  variant?: PriceVariant
  className?: string
}

const Price = ({
  value,
  currency,
  currencyDisplay = currencyDisplayDefault,
  decimalDash = decimalDashDefault,
  useGrouping = useGroupingDefault,
  language,
  variant = 'regular',
  ...others
}: Props) => {
  const defaultCurrencyCode = useReactiveVar(currencyCode)

  // Based on https://github.com/lingui/js-lingui/blob/afc05d57336ab38f648bcfe52711f80d230fa47d/packages/core/src/formats.js#L31
  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(language || i18n.locale, {
        style: 'currency',
        currency: currency || defaultCurrencyCode,
        currencyDisplay:
          currencyDisplay !== 'none' ? currencyDisplay : undefined,
        useGrouping,
      }),
    [currency, currencyDisplay, defaultCurrencyCode, language, useGrouping],
  )

  if (!formatter.formatToParts) {
    // IE11
    return (
      <PriceContainer variant={variant} {...others}>
        {formatter.format(value)}
      </PriceContainer>
    )
  }

  let parts = formatter.formatToParts(value)
  if (typeof parts[0] !== 'object') {
    // Safari 12 and perhaps older version
    return (
      <PriceContainer variant={variant} {...others}>
        {formatter.format(value)}
      </PriceContainer>
    )
  }

  if (currencyDisplay === 'none') {
    parts = parts.filter(({ type }) => type !== 'currency')
    // Strip trailing whitespaces
    parts = parts.filter(({ type, value }, index) => {
      if (index === 0 || index === parts.length) {
        // First or last item

        if (type === 'literal' && value.trim() === '') {
          return false
        }
      }
      return true
    })
  }

  return (
    <PriceContainer variant={variant} {...others}>
      {parts.map(({ type, value }, index) => (
        <span key={`${index}-${type}-${value}`} className={type}>
          {decimalDash && type === 'fraction' && value === '00' ? '-' : value}
        </span>
      ))}
    </PriceContainer>
  )
}

export default overrideOptional(Price)
